import { useCallback, useMemo } from "react";
import { useI18n } from "./I18nContext";
import {
  getProductTranslation,
  getCategoryTranslation,
  translateProduct,
  translateVariant,
  productTranslations,
} from "./productTranslations";

export function useProductTranslation() {
  const { language, t } = useI18n();

  const translateField = useCallback(
    (productId, fieldName, defaultValue) => {
      return getProductTranslation(productId, fieldName, language, defaultValue);
    },
    [language]
  );

  const translateCategory = useCallback(
    (slug) => {
      return getCategoryTranslation(slug, language);
    },
    [language]
  );

  const getTranslatedProduct = useCallback(
    (product) => {
      return translateProduct(product, language);
    },
    [language]
  );

  const getTranslatedVariant = useCallback(
    (variant, productId) => {
      return translateVariant(variant, language, productId);
    },
    [language]
  );

  const getVariantTypeLabel = useCallback(
    (isNext) => {
      const key = isNext ? "Seguinte" : "Base";
      return t(key);
    },
    [t]
  );

  const getTranslatedFeatures = useCallback(
    (product, features) => {
      if (!features || language === "pt") return features;
      const productId = product.id || product.sku;
      return features.map((f) => getProductTranslation(productId, `feature_${f}`, language, f));
    },
    [language]
  );

  // Helper to translate review rating labels
  const getRatingLabel = useCallback(
    (rating) => {
      const labels = {
        5: t("Excelente"),
        4: t("Muito bom"),
        3: t("Médio"),
        2: t("Fraco"),
        1: t("Muito fraco"),
      };
      return labels[rating] || rating;
    },
    [t]
  );

  return {
    language,
    t,
    translateField,
    translateCategory,
    getTranslatedProduct,
    getTranslatedVariant,
    getVariantTypeLabel,
    getTranslatedFeatures,
    getRatingLabel,
    productTranslations,
  };
}

// Helper component to translate product on the fly
export function TranslatedProduct({ product, children }) {
  const { getTranslatedProduct } = useProductTranslation();
  const translatedProduct = useMemo(() => getTranslatedProduct(product), [getTranslatedProduct, product]);
  return children(translatedProduct);
}

// Helper component to translate variant on the fly
export function TranslatedVariant({ variant, productId, children }) {
  const { getTranslatedVariant } = useProductTranslation();
  const translatedVariant = useMemo(() => getTranslatedVariant(variant, productId), [getTranslatedVariant, variant, productId]);
  return children(translatedVariant);
}